/**
 * Ficha de producto o servicio: alta, edición, (des)activación y borrado —
 * `TDS-CORE` Parte 2 §1, `OD-0029 D-02`.
 *
 * - Tipos y causas salen del catálogo que devuelve la API: la pantalla no
 *   conoce tipos de IVA por su cuenta.
 * - El precio se escribe y se envía como TEXTO: el navegador nunca lo convierte
 *   a número.
 * - Las causas se muestran por su código (E1..E6, N1, N2): sus textos oficiales
 *   están pendientes de registrar (DF-04) y no se inventan.
 * - Clave de idempotencia fija por formulario de alta (`TDS-000 §5.1`).
 */
import { useEffect, useState } from "react";
import {
  api,
  ESTADO_VALIDACION,
  type CatalogoIva,
  type DatosProducto,
  type ErrorApi,
  type Producto,
  type TratamientoIva,
} from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Campo, Casilla, Selector, Tarjeta } from "../ui/primitivas";

const VACIO: DatosProducto = {
  code: "",
  description: "",
  unitPrice: "",
  vatTreatment: "SUJETA_NO_EXENTA",
  vatRate: "21,00",
  vatCause: null,
  withholdingDefault: false,
};

const TRATAMIENTOS: ReadonlyArray<{ readonly valor: TratamientoIva; readonly texto: string }> = (
  ["SUJETA_NO_EXENTA", "SUJETA_EXENTA", "NO_SUJETA"] as const
).map((v) => ({ valor: v, texto: t.producto.tratamientos[v] }));

function datosDe(p: Producto): DatosProducto {
  return {
    code: p.code,
    description: p.description,
    unitPrice: p.unitPrice,
    vatTreatment: p.vatTreatment,
    vatRate: p.vatRate,
    vatCause: p.vatCause,
    withholdingDefault: p.withholdingDefault,
  };
}

/** Sólo se envía lo que corresponde al tratamiento elegido. */
function limpiar(d: DatosProducto): DatosProducto {
  return d.vatTreatment === "SUJETA_NO_EXENTA" ? { ...d, vatCause: null } : { ...d, vatRate: null };
}

export function PantallaProducto({
  id,
  hayConexion,
  onVolver,
  onAbrir,
  onSesionCaducada,
}: {
  /** `null` = alta. */
  id: string | null;
  hayConexion: boolean;
  onVolver: () => void;
  onAbrir: (id: string) => void;
  onSesionCaducada: () => void;
}) {
  const [producto, setProducto] = useState<Producto | null>(null);
  const [datos, setDatos] = useState<DatosProducto>(VACIO);
  const [catalogo, setCatalogo] = useState<CatalogoIva | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<{ tono: "exito" | "error" | "aviso" | "info"; texto: string } | null>(null);
  const [duplicadoDe, setDuplicadoDe] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  const [clave] = useState(() => crypto.randomUUID());

  useEffect(() => {
    void (async () => {
      const [cat, prod] = await Promise.all([api.catalogoIva(), id === null ? Promise.resolve(null) : api.obtenerProducto(id)]);
      setCargando(false);
      if (!cat.ok) {
        if (cat.estado === 401) return onSesionCaducada();
        setMensaje({ tono: "error", texto: t.producto.sinCatalogo });
      } else setCatalogo(cat.datos);
      if (prod === null) return;
      if (prod.ok) {
        setProducto(prod.datos);
        setDatos(datosDe(prod.datos));
      } else if (prod.estado === 401) onSesionCaducada();
      else setMensaje({ tono: "error", texto: prod.estado === 404 ? t.producto.noEncontrado : t.comun.errorGenerico });
    })();
  }, [id, onSesionCaducada]);

  const texto = (campo: "code" | "description" | "unitPrice") => (v: string) => setDatos((d) => ({ ...d, [campo]: v }));
  const errorDe = (campo: string): string | null =>
    errores[campo] === undefined ? null : (t.producto.errores[errores[campo]] ?? t.comun.errorGenerico);

  function rechazo(r: { estado: number; error: ErrorApi | null }): void {
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setMensaje({ tono: "aviso", texto: t.producto.sinPermiso });
    if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores(r.error.campos);
    if (r.estado === 409 && r.error?.motivo === "DUPLICATE_CODE") {
      const existente = r.error.detalles.existingProductId;
      setDuplicadoDe(typeof existente === "string" ? existente : null);
      return setMensaje({ tono: "aviso", texto: t.producto.duplicado });
    }
    if (r.estado === 409 && r.error?.motivo === "PRODUCT_IN_USE") return setMensaje({ tono: "aviso", texto: t.producto.enUso });
    // 429 QUOTA_EXCEEDED: el mensaje de la API ya dice el límite y qué hacer (URS §110).
    if (r.estado === 429 && r.error !== null) return setMensaje({ tono: "aviso", texto: `${r.error.message} ${r.error.action ?? ""}`.trim() });
    setMensaje({ tono: "error", texto: r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico });
  }

  function limpiarAvisos(): void {
    setMensaje(null);
    setErrores({});
    setDuplicadoDe(null);
  }

  async function guardar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    limpiarAvisos();
    setOcupado(true);
    const cuerpo = limpiar(datos);
    const r = producto === null ? await api.crearProducto(cuerpo, clave) : await api.editarProducto(producto.id, cuerpo);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    const eraAlta = producto === null;
    setProducto(r.datos);
    setDatos(datosDe(r.datos));
    setMensaje({ tono: "exito", texto: eraAlta ? t.producto.creado : t.producto.guardado });
  }

  async function cambiarActivo(activo: boolean): Promise<void> {
    if (producto === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.activarProducto(producto.id, activo);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    setProducto(r.datos);
    setMensaje({ tono: "info", texto: activo ? t.producto.activado : t.producto.desactivado });
  }

  async function eliminar(): Promise<void> {
    if (producto === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.eliminarProducto(producto.id);
    setOcupado(false);
    setConfirmandoBorrado(false);
    if (!r.ok) return rechazo(r);
    onVolver();
  }

  if (cargando) {
    return (
      <Tarjeta titulo={id === null ? t.producto.tituloNuevo : t.producto.tituloEditar}>
        <p>{t.productos.cargando}</p>
      </Tarjeta>
    );
  }

  const tipos = (catalogo?.vatRates ?? []).map((v) => ({ valor: v, texto: t.producto.tipoOpcion(v) }));
  const causas = (datos.vatTreatment === "SUJETA_EXENTA" ? catalogo?.exemptionCauses : catalogo?.nonSubjectCauses) ?? [];
  const opcionesCausa = [{ valor: "", texto: "—" }, ...causas.map((c) => ({ valor: c, texto: c }))];

  return (
    <>
      <Boton variante="secundario" onClick={onVolver}>
        {t.producto.volver}
      </Boton>
      <Tarjeta titulo={producto === null ? t.producto.tituloNuevo : t.producto.tituloEditar}>
        <form onSubmit={guardar} noValidate>
          <div className="fila-campos">
            <Campo id="prd-codigo" etiqueta={t.producto.code} valor={datos.code} onChange={texto("code")} ayuda={t.producto.ayudaCode} autoComplete="off" error={errorDe("code")} />
            <Campo id="prd-precio" etiqueta={t.producto.unitPrice} valor={datos.unitPrice} onChange={texto("unitPrice")} ayuda={t.producto.ayudaPrecio} inputMode="text" autoComplete="off" error={errorDe("unitPrice")} />
          </div>
          <Campo id="prd-descripcion" etiqueta={t.producto.description} valor={datos.description} onChange={texto("description")} ayuda={t.producto.ayudaDescription} autoComplete="off" error={errorDe("description")} />
          <div className="fila-campos">
            <Selector
              id="prd-tratamiento"
              etiqueta={t.producto.tratamiento}
              valor={datos.vatTreatment}
              opciones={TRATAMIENTOS}
              onChange={(v) =>
                setDatos((d) => ({
                  ...d,
                  vatTreatment: v,
                  vatRate: v === "SUJETA_NO_EXENTA" ? (d.vatRate ?? catalogo?.vatRates[0] ?? null) : null,
                  vatCause: null,
                }))
              }
              error={errorDe("vatTreatment")}
            />
            {datos.vatTreatment === "SUJETA_NO_EXENTA" ? (
              <Selector
                id="prd-tipo"
                etiqueta={t.producto.tipo}
                valor={datos.vatRate ?? ""}
                opciones={tipos}
                onChange={(v) => setDatos((d) => ({ ...d, vatRate: v }))}
                error={errorDe("vatRate")}
              />
            ) : (
              <Selector
                id="prd-causa"
                etiqueta={t.producto.causa}
                valor={datos.vatCause ?? ""}
                opciones={opcionesCausa}
                onChange={(v) => setDatos((d) => ({ ...d, vatCause: v === "" ? null : v }))}
                error={errorDe("vatCause")}
              />
            )}
          </div>
          {datos.vatTreatment !== "SUJETA_NO_EXENTA" ? (
            <>
              <p className="ayuda-neutra">{t.producto.ayudaCausa}</p>
              <Aviso tono="aviso">{t.producto.ayudaExenta}</Aviso>
            </>
          ) : null}
          <Casilla
            id="prd-retencion"
            etiqueta={t.producto.retencion}
            marcada={datos.withholdingDefault}
            onChange={(v) => setDatos((d) => ({ ...d, withholdingDefault: v }))}
            error={errorDe("withholdingDefault")}
          />
          <p className="ayuda-neutra">{t.producto.ayudaRetencion}</p>

          {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
          {duplicadoDe !== null ? (
            <Boton variante="secundario" onClick={() => onAbrir(duplicadoDe)}>
              {t.producto.abrirExistente}
            </Boton>
          ) : null}
          {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
          <Boton tipo="submit" deshabilitado={ocupado || !hayConexion || catalogo === null}>
            {ocupado ? t.producto.guardando : t.producto.guardar}
          </Boton>
        </form>
      </Tarjeta>

      {producto !== null ? (
        <Tarjeta titulo={producto.active ? t.producto.desactivar : t.producto.activar}>
          <p className="ayuda-neutra">{t.producto.desactivarDetalle}</p>
          <Boton variante="secundario" onClick={() => void cambiarActivo(!producto.active)} deshabilitado={ocupado || !hayConexion}>
            {producto.active ? t.producto.desactivar : t.producto.activar}
          </Boton>
        </Tarjeta>
      ) : null}

      {producto !== null ? (
        <Tarjeta titulo={t.producto.eliminar}>
          {!confirmandoBorrado ? (
            <Boton variante="secundario" onClick={() => setConfirmandoBorrado(true)} deshabilitado={ocupado || !hayConexion}>
              {t.producto.eliminar}
            </Boton>
          ) : (
            <>
              <Aviso tono="aviso">{t.producto.eliminarPregunta}</Aviso>
              <div className="acciones">
                <Boton onClick={() => void eliminar()} deshabilitado={ocupado || !hayConexion}>
                  {t.producto.siEliminar}
                </Boton>
                <Boton variante="secundario" onClick={() => setConfirmandoBorrado(false)}>
                  {t.producto.cancelar}
                </Boton>
              </div>
            </>
          )}
        </Tarjeta>
      ) : null}
    </>
  );
}
